import { NotifyHelper } from 'classes/helpers/notify.helper';
import { WebSocketHelper } from 'classes/helpers/web-socket.helper';
import { MachineContext } from 'contexts/machine.context';
import useCalibrationListener from 'hooks/machine/useCalibrationListener';
import { ITrainingMsgExt } from 'interfaces/i-training';
import { WsMsgType } from 'lib_ts/enums/machine-msg.enum';
import { IFireResponseMsg } from 'lib_ts/interfaces/machine-msg/i-fire-response';
import { IOverlaySummaryResponseMsg } from 'lib_ts/interfaces/machine-msg/i-overlay-summary';
import { IProcessQueryResponseMsg } from 'lib_ts/interfaces/machine-msg/i-process-status';
import {
  ISoftRebootResponseMsg,
  ISystemRebootResponseMsg,
} from 'lib_ts/interfaces/machine-msg/i-reboot';
import { useContext, useEffect } from 'react';
import { useAuthStore } from 'stores/use-auth-store';
import useMachineStatusStore from 'stores/use-machine-status-store';

export const MachineResponseListener = () => {
  useCalibrationListener();

  const wsConnected = useAuthStore((s) => s.wsConnected);
  const { setOverlayIDs, setProcessData } = useContext(MachineContext);

  useEffect(() => {
    if (!wsConnected) {
      return;
    }

    const listeners: {
      type: WsMsgType;
      callback: (event: CustomEvent) => void;
    }[] = [
      {
        type: WsMsgType.M2U_FireResponse,
        callback: (event: CustomEvent) => {
          const data: IFireResponseMsg = event.detail;
          console.log(
            `* Fire response received - Success: ${data.status}`,
            data.message ? `(${data.message})` : ''
          );

          useMachineStatusStore.setState({
            fireStateDates: {
              ...useMachineStatusStore.getState().fireStateDates,
              awaitFiringUntil: new Date(),
            },
          });
        },
      },
      {
        type: WsMsgType.M2U_TrainingResponse,
        callback: (event: CustomEvent) => {
          const data: ITrainingMsgExt = event.detail;
          console.log(
            `* Training response received - ${data.message} (fireID: ${
              data.fire._id
            }${data.shot ? `, shotID: ${data.shot._id}` : ''}, dateCreated: ${
              data.fire._created
            })`
          );
        },
      },
      {
        type: WsMsgType.M2U_OverlaysResponse,
        callback: (event: CustomEvent) => {
          const data: IOverlaySummaryResponseMsg = event.detail;
          setOverlayIDs(data.overlay_ids);
        },
      },
      {
        type: WsMsgType.M2U_QueryProcesses,
        callback: (event: CustomEvent) => {
          const data: IProcessQueryResponseMsg = event.detail;
          setProcessData(data);
        },
      },
      {
        // aka: restart OS
        type: WsMsgType.M2U_SoftReboot,
        callback: (event: CustomEvent) => {
          const data: ISoftRebootResponseMsg = event.detail;
          if (!data.success) {
            NotifyHelper.error({ message_md: 'Failed to restart.' });
            return;
          }

          NotifyHelper.success({
            message_md: 'Restart requested, please wait',
          });
        },
      },
      {
        type: WsMsgType.M2U_SystemRestart,
        callback: (event: CustomEvent) => {
          const data: ISystemRebootResponseMsg = event.detail;

          if (!data.success) {
            NotifyHelper.error({
              message_md: `Restart failed on processes ${data.failed_list}.`,
            });
            return;
          }

          NotifyHelper.success({ message_md: 'Restart successful.' });
        },
      },
    ];

    listeners.forEach((m) => {
      WebSocketHelper.on(m.type, m.callback);
    });

    return () => {
      listeners.forEach((m) => {
        WebSocketHelper.remove(m.type, m.callback);
      });
    };
  }, [wsConnected]);

  return null;
};
